import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { PDFDownloadLink, PDFViewer } from '@react-pdf/renderer';
import { Invoice } from '../types';
import { invoiceAPI } from '../services/api';
import InvoicePDF from './InvoicePDF';

const InvoiceDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showPDFPreview, setShowPDFPreview] = useState(false);

  useEffect(() => {
    if (id) {
      fetchInvoice(parseInt(id));
    }
  }, [id]);

  const fetchInvoice = async (invoiceId: number) => {
    try {
      setLoading(true);
      const response = await invoiceAPI.getById(invoiceId);
      setInvoice(response.data);
      setError(null);
    } catch (err) {
      setError('Failed to fetch invoice');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="container">
        <div className="card">
          <p>Loading invoice...</p>
        </div>
      </div>
    );
  }

  if (error || !invoice) {
    return (
      <div className="container">
        <div className="card">
          <div className="error">{error || 'Invoice not found'}</div>
          <Link to="/invoices" className="btn btn-secondary">
            Back to Invoices
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <div className="card">
        {/* Action Buttons */}
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2rem' }}>
          <div>
            <Link to="/invoices" className="btn btn-secondary">
              ← Back to Invoices
            </Link>
            <Link to={`/invoices/${invoice.id}/edit`} className="btn btn-primary" style={{ marginLeft: '1rem' }}>
              Edit Invoice
            </Link>
          </div>
          <div>
            <button
              onClick={() => setShowPDFPreview(!showPDFPreview)}
              className="btn btn-secondary"
              style={{ marginRight: '1rem' }}
            >
              {showPDFPreview ? 'Hide Preview' : 'Preview PDF'}
            </button>
            <PDFDownloadLink
              document={<InvoicePDF invoice={invoice} />}
              fileName={`invoice-${invoice.invoice_number}.pdf`}
              className="btn btn-success"
            >
              {({ loading }) => <>{loading ? 'Preparing PDF...' : 'Download PDF'}</>}
            </PDFDownloadLink>
          </div>
        </div>

        {/* PDF Preview */}
        {showPDFPreview && (
          <div style={{ marginBottom: '2rem', border: '1px solid #ddd', borderRadius: '4px', overflow: 'hidden' }}>
            <PDFViewer width="100%" height="600px">
              <InvoicePDF invoice={invoice} />
            </PDFViewer>
          </div>
        )}

        {/* Invoice Header */}
        <div style={{ marginBottom: '2rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h2>Invoice {invoice.invoice_number}</h2>
            <span className={`status-badge status-${invoice.status}`}>{invoice.status}</span>
          </div>
        </div>

        {/* Invoice Details Grid */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem', marginBottom: '2rem' }}>
          {/* Bill To Section */}
          <div>
            <h3 style={{ marginBottom: '1rem', color: '#2c3e50' }}>Bill To</h3>
            <div style={{ padding: '1rem', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
              <p style={{ margin: '0.25rem 0', fontWeight: 'bold' }}>{invoice.customer_details?.name}</p>
              {invoice.customer_details?.address_line1 && (
                <p style={{ margin: '0.25rem 0' }}>{invoice.customer_details.address_line1}</p>
              )}
              {invoice.customer_details?.address_line2 && (
                <p style={{ margin: '0.25rem 0' }}>{invoice.customer_details.address_line2}</p>
              )}
              {invoice.customer_details?.address_line3 && (
                <p style={{ margin: '0.25rem 0' }}>{invoice.customer_details.address_line3}</p>
              )}
              <p style={{ margin: '0.25rem 0' }}>
                {invoice.customer_details?.city}, {invoice.customer_details?.state}{' '}
                {invoice.customer_details?.zip_code}
              </p>
              {invoice.customer_details?.email && (
                <p style={{ margin: '0.25rem 0' }}>{invoice.customer_details.email}</p>
              )}
              {invoice.customer_details?.phone && (
                <p style={{ margin: '0.25rem 0' }}>{invoice.customer_details.phone}</p>
              )}
            </div>
          </div>

          {/* Invoice Details Section */}
          <div>
            <h3 style={{ marginBottom: '1rem', color: '#2c3e50' }}>Invoice Details</h3>
            <div style={{ padding: '1rem', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', margin: '0.5rem 0' }}>
                <strong>Invoice Number:</strong>
                <span>{invoice.invoice_number}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', margin: '0.5rem 0' }}>
                <strong>PO Number:</strong>
                <span>{invoice.po_details?.po_number}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', margin: '0.5rem 0' }}>
                <strong>Issue Date:</strong>
                <span>{formatDate(invoice.issue_date)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', margin: '0.5rem 0' }}>
                <strong>Due Date:</strong>
                <span>{formatDate(invoice.due_date)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', margin: '0.5rem 0' }}>
                <strong>Payment Terms:</strong>
                <span>{invoice.po_details?.payment_terms_display}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Line Items Table */}
        <div style={{ marginBottom: '2rem' }}>
          <h3 style={{ marginBottom: '1rem', color: '#2c3e50' }}>Line Items</h3>
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left' }}>Description</th>
                  <th style={{ textAlign: 'right' }}>Quantity</th>
                  <th style={{ textAlign: 'right' }}>Unit Price</th>
                  <th style={{ textAlign: 'right' }}>Total</th>
                </tr>
              </thead>
              <tbody>
                {invoice.line_items.map((item, index) => (
                  <tr key={item.id || index}>
                    <td>{item.description}</td>
                    <td style={{ textAlign: 'right' }}>{item.quantity}</td>
                    <td style={{ textAlign: 'right' }}>{formatCurrency(Number(item.unit_price))}</td>
                    <td style={{ textAlign: 'right' }}>{formatCurrency(item.total || 0)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Totals Section */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '2rem' }}>
          <div style={{ width: '300px', padding: '1rem', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', margin: '0.5rem 0' }}>
              <span>Subtotal:</span>
              <strong>{formatCurrency(invoice.subtotal || 0)}</strong>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', margin: '0.5rem 0' }}>
              <span>Tax ({invoice.tax_rate}%):</span>
              <strong>{formatCurrency(invoice.tax_amount || 0)}</strong>
            </div>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                margin: '0.5rem 0',
                paddingTop: '0.5rem',
                borderTop: '2px solid #2c3e50',
                fontSize: '1.2rem',
              }}
            >
              <strong>Total Due:</strong>
              <strong style={{ color: '#27ae60' }}>{formatCurrency(invoice.total_amount || 0)}</strong>
            </div>
          </div>
        </div>

        {/* Notes Section */}
        {invoice.notes && (
          <div style={{ padding: '1rem', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
            <h3 style={{ marginBottom: '0.5rem', color: '#2c3e50' }}>Notes</h3>
            <p style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{invoice.notes}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default InvoiceDetail;
